import { Spin } from "./Spin";
import { forwardRef, type CSSProperties, type MouseEvent } from "react";

import { cx, useKitDisabled } from "./fieldContext";
import { useUiText } from "./uiText";

export interface ActiveToggleProps {
  checked: boolean;
  onChange?: (checked: boolean, event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  /** Zugänglicher Name, z. B. „Kampagne aktiv“; sichtbar steht nur der Zustand. */
  "aria-label"?: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Aktivzustand eines einzelnen Objekts im Kopf von Drawer, Assistent oder Detailseite (14.10.2026):
 * ein Statuspunkt mit „aktiv“/„inaktiv“, der selbst der Schalter ist — kein Label plus Schalter daneben.
 * Sonst gilt DNA D31: `Switch` nur bei Sofortwirkung (Spalte „aktiv“), Formularfelder sind `Checkbox`.
 */
export const ActiveToggle = forwardRef<HTMLButtonElement, ActiveToggleProps>(function ActiveToggle(
  { checked, onChange, disabled, loading, className, ...rest },
  ref,
) {
  const { text } = useUiText();
  const mergedDisabled = useKitDisabled(disabled);
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-busy={loading || undefined}
      disabled={mergedDisabled || loading}
      className={cx("ck-active-toggle", checked && "is-on", className)}
      onClick={(event) => onChange?.(!checked, event)}
      {...rest}
    >
      <span className="ck-active-toggle-dot" aria-hidden="true">{loading ? <Spin inline /> : null}</span>
      <span className="ck-active-toggle-text">{text(checked ? "active" : "inactive")}</span>
    </button>
  );
});
